import * as React from 'react';
import { css } from '@emotion/react';
import { StarFilledIcon } from '../../icons/StarFilledIcon';
import { FixMeLater, startDirection } from '../../utils';
import { ColorOption, useHoneycombTheme } from '../../theming';

import { SvgIconProps } from '../SvgIcon/SvgIcon';

export type StarRatingTheme = {
    iconSize: number;
    color: ColorOption;
    disabledColor: ColorOption;
    spacing: number;
};

const starRatingTheme = {
    iconSize: 12,
    color: 'starRating',
    disabledColor: 'grey.light',
    spacing: 3,
};

export type StarRatingProps = {
    /**
     * The icon to use.
     * @default <StarFilledIcon />
     */
    icon?: React.ReactElement<SvgIconProps>;

    /**
     * The rating to be applied.
     * @default 1
     */
    rating?: number;
};

/**
 * Following component will display the given number (from input props) of stars for the user, it also can be extended out of just
 * showing star rating as it supports taking in any icon and display x instances of that icon as a result.
 */

export const StarRating: React.ForwardRefExoticComponent<StarRatingProps & React.RefAttributes<HTMLElement>> =
    React.forwardRef(function StarRating(
        props: StarRatingProps,
        ref: React.ForwardedRef<FixMeLater>
    ): React.JSX.Element {
        const { rating = 1, icon = <StarFilledIcon />, ...other } = props;

        const ceiling = Math.ceil(rating as number);
        const halfRating = rating < ceiling;
        const total = halfRating ? ceiling + 1 : ceiling;

        const { iconSize, color: colorName, spacing } = starRatingTheme;
        const { theme } = useHoneycombTheme();
        const color = theme.fn.getColor(colorName as ColorOption);
        const start = startDirection();

        const rootStyles = css`
            display: inline-flex;
            height: ${iconSize}px;
            color: ${color};
            > svg {
                width: ${iconSize}px;
                height: ${iconSize}px;
                flex-shrink: 0;
            }
            > svg + svg {
                margin-${start}: ${spacing}px;
            }
        `;

        const styles = [rootStyles];

        if (halfRating) {
            const disabledColor = theme.fn.getColor(starRatingTheme.disabledColor as ColorOption);
            const halfRatingStyles = css`
                > svg:nth-last-of-type(2) {
                    clip-path: inset(0 50% 0 0);
                }
                > svg:nth-last-of-type(1) {
                    fill: ${disabledColor};
                    margin-${start}: ${iconSize * -1}px;
                    clip-path: inset(0 0 0 50%);
                }
            `;
            styles.push(halfRatingStyles);
        }

        return (
            <span ref={ref} css={styles} {...other}>
                {[...Array(total)].map((_, index) => React.cloneElement(icon, { key: index.toString() }))}
            </span>
        );
    });
